import { Checkbox } from '@fluentui/react-components';
import { GridRegular, TextBulletListLtrRegular } from '@fluentui/react-icons';
import {
  ListPresentation,
  UserListSorting,
  sortUsersByPreference,
} from '@mnspro-cloud/services';
import {
  AbsenceReasonCategory,
  AbsenceReasonResponse,
  AbsenceResponse,
  AbsenceState,
  LessonResponse,
} from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import cn from 'classnames';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../Button';
import { useConfirmationDialog } from '../../../hooks';
import { Loader } from '../../../Loader/Loader';
import { SearchInput } from '../../../SearchInput/SearchInput';
import { Text } from '../../../Text';
import { StudentAbsences } from './AbsenceEditDialog.interfaces';
import './AbsenceEditDialog.scss';
import {
  getGroupedAbsences,
  getUpdatedAbsenceList,
  updateAbsencesListWithLateOrLeftEarlyAbsence,
} from './AbsencesEditDialog.utils';
import { LateEarlyCreateOrUpdateDecisionDialog } from './LateEarlyCreateOrUpdateDecisionDialog';
import { NoAbsencesFound } from './NoAbsencesFound';
import { StudentAbsenceButton } from './StudentAbsenceButton';
import { StudentLeftEarlyDialog } from './StudentLeftEarlyDialog';

export interface AbsenceEditDialogProps {
  onCloseClick: () => void;
  onSaveClick: (AbsenceClassbookEntry: AbsenceResponse[], allUncheckedToPresent?: boolean) => void;
  absenceReasons: AbsenceReasonResponse[];
  allAbsences: AbsenceResponse[];
  disabled: boolean;
  isUpdating?: boolean;
  currentLesson: LessonResponse | undefined;
  listpresentation: ListPresentation;
  onListPresentationChange: (listpresentation: ListPresentation) => void;
  preferenceSorting?: UserListSorting;
  isLoading?: boolean;
}

// Sort the student list according to the user's personal name-sorting
// preference (firstname-first vs lastname-first), defaulting to firstname.
const sortByPreference = (list: StudentAbsences[], preferenceSorting?: UserListSorting) =>
  sortUsersByPreference(
    list,
    preferenceSorting ?? UserListSorting.FirstName,
    (item) => item.student,
  );

export const AbsenceEditDialog: React.FC<AbsenceEditDialogProps> = ({
  onCloseClick,
  onSaveClick,
  absenceReasons,
  allAbsences,
  disabled,
  isUpdating,
  currentLesson,
  listpresentation,
  onListPresentationChange,
  preferenceSorting,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [visibleAbsences, setVisibleAbsences] = useState<StudentAbsences[]>([]);
  const [searchString, setSearchString] = useState('');
  const [allToPresent, setAllToPresent] = useState(true);
  const [lateOrLeftEarlyAbsence, setLateOrLeftEarlyAbsence] = useState<AbsenceResponse>();
  const [ambiguousAbsence, setAmbiguousAbsence] = useState<AbsenceResponse>();
  const [replaceAbsence, setReplaceAbsence] = useState(false);
  const { getConfirmation } = useConfirmationDialog();

  useEffect(() => {
    const groupedAbsences = getGroupedAbsences(allAbsences);
    setVisibleAbsences(sortByPreference(groupedAbsences, preferenceSorting));
  }, [allAbsences, preferenceSorting]);

  const handleConfirm = () => {
    const pureAbsences = visibleAbsences.flatMap((studentAbsences) => studentAbsences.absences);
    let absences = [...pureAbsences];

    if (allToPresent) {
      absences = pureAbsences.map((absence) => ({
        ...absence,
        absenceReason:
          absence.absenceReason === undefined || absence.absenceReason === null
            ? null
            : absence.absenceReason,
      }));
    }

    onSaveClick(absences, allToPresent);
    setAllToPresent(true);
  };

  const handleAbsenceReasonSelected = async (
    absenceReason: AbsenceReasonResponse | null,
    absence: AbsenceResponse,
  ) => {
    if (currentLesson?.multiLessons && currentLesson.multiLessons.length > 0) {
      // Temporary not supported in multilesson scenario
      // will be added in feature
      const studentAbsences = visibleAbsences.filter(
        (a) => a.student.id === absence.affectedStudent.id,
      );
      const hasLateAndLeftEarly = studentAbsences.every((a) => {
        const categoryAbsences = a.absences.filter((b) => b.absenceReason?.category);
        if (categoryAbsences.length < 2) {
          return false;
        }
        return categoryAbsences.some(
          (lateOrLeftEarly) =>
            lateOrLeftEarly.absenceReason?.category === AbsenceReasonCategory.leftEarly ||
            lateOrLeftEarly.absenceReason?.category === AbsenceReasonCategory.late,
        );
      });

      const mayAddSecondOffSetAbsence =
        absence.absenceReason?.category !== absenceReason?.category &&
        absenceReason?.category &&
        (absenceReason?.category === AbsenceReasonCategory.late ||
          absenceReason?.category === AbsenceReasonCategory.leftEarly) &&
        (absence.absenceReason?.category === AbsenceReasonCategory.late ||
          absence.absenceReason?.category === AbsenceReasonCategory.leftEarly);

      if (hasLateAndLeftEarly || mayAddSecondOffSetAbsence) {
        await getConfirmation({
          content: t('classbook:absences:editdialog:multilesson_not_supported', {
            defaultValue:
              'Derzeit ist die Bearbeitung der Kombination aus "Geht früher" und "Verspätet" nur über den Bereich "Anwesenheiten" möglich.',
          }),
          headerText: t('classbook:absences:editdialog:multilesson_not_supported_header', {
            defaultValue: 'Bearbeitung nicht möglich',
          }),
          confirmButtonText: t('confirm', { defaultValue: 'Bestätigen' }),
          cancelButtonText: t('cancel', { defaultValue: 'Abbrechen' }),
          showCloseButton: false,
        });

        return;
      }
    }

    if (
      absenceReason &&
      (absenceReason.category === AbsenceReasonCategory.leftEarly ||
        absenceReason.category === AbsenceReasonCategory.late)
    ) {
      //If Late or left early
      const absenceToModify = {
        ...absence,
        absenceReason,
        state: AbsenceState.absent,
      };
      const originalAbsenceReasonCategory = absence.absenceReason?.category;
      if (
        (originalAbsenceReasonCategory === AbsenceReasonCategory.late &&
          absenceReason.category === AbsenceReasonCategory.leftEarly) ||
        (originalAbsenceReasonCategory === AbsenceReasonCategory.leftEarly &&
          absenceReason.category === AbsenceReasonCategory.late)
      ) {
        setAmbiguousAbsence(absenceToModify);
      } else {
        setReplaceAbsence(true);
        setLateOrLeftEarlyAbsence(absenceToModify);
      }
    } else {
      //If unknown (?) or none or present... whatever is not late or early
      //replace existing absence (?)
      //Existing absence
      //is only one or none
      const updatedAbsencesList = getUpdatedAbsenceList(visibleAbsences, absence, absenceReason);

      setVisibleAbsences(sortByPreference(updatedAbsencesList, preferenceSorting));
    }
  };

  const closeHandleStudentLeftEarlyDialog = () => {
    setLateOrLeftEarlyAbsence(undefined);
  };

  const handleStudentLeftEarlyDialogCloseClick = () => closeHandleStudentLeftEarlyDialog();

  const handleStudentLeftEarlyDialogSaveClick = (minutes: number) => {
    if (lateOrLeftEarlyAbsence && minutes > 0) {
      //check if lesson is multilesson if true we need to update start and enddate for Late or left early
      if (
        currentLesson?.multiLessons &&
        currentLesson.multiLessons.length > 0 &&
        lateOrLeftEarlyAbsence.absenceReason
      ) {
        lateOrLeftEarlyAbsence.start =
          lateOrLeftEarlyAbsence.absenceReason.category === AbsenceReasonCategory.late
            ? currentLesson.start
            : currentLesson.multiLessons[0].start;
        lateOrLeftEarlyAbsence.end =
          lateOrLeftEarlyAbsence.absenceReason.category === AbsenceReasonCategory.late
            ? currentLesson.end
            : currentLesson.multiLessons[0].end;
      }

      const updatedAbsences = updateAbsencesListWithLateOrLeftEarlyAbsence(
        lateOrLeftEarlyAbsence,
        minutes,
        visibleAbsences,
        replaceAbsence,
      );
      setVisibleAbsences(sortByPreference(updatedAbsences, preferenceSorting));
    }

    closeHandleStudentLeftEarlyDialog();
  };

  const handleAmbiguousAbsence = () => {
    setLateOrLeftEarlyAbsence(ambiguousAbsence);
    //close dialog
    setAmbiguousAbsence(undefined);
  };

  const handleAddAbsenceToExisting = () => {
    //open lateOrLeftDialog
    setReplaceAbsence(false);
    handleAmbiguousAbsence();
  };
  const handleCancelLateEarlyDecisionDialog = () => {
    //close dialog
    setAmbiguousAbsence(undefined);
  };
  const handleReplaceAbsence = () => {
    setReplaceAbsence(true);
    handleAmbiguousAbsence();
  };

  const studentAbsencesButtons = sortByPreference(visibleAbsences, preferenceSorting)
    .filter((item) => item.student.displayName.toLowerCase().includes(searchString.toLowerCase()))
    .map((studentAbsence) => (
      <StudentAbsenceButton
        key={studentAbsence.student.id}
        studentAbsences={studentAbsence}
        absenceReasons={absenceReasons}
        disabled={disabled}
        absenceReasonSelected={handleAbsenceReasonSelected}
        isUpdating={isUpdating}
        preferenceSorting={preferenceSorting}
      />
    ));

  const content =
    studentAbsencesButtons.length > 0 ? (
      <div
        className={cn({
          'studentsListWrapperGrid contentBox': listpresentation === ListPresentation.Grid,
          'studentsListWrapperList contentBox': listpresentation === ListPresentation.List,
        })}
      >
        {studentAbsencesButtons}
      </div>
    ) : (
      <div className="flex contentBox">
        <NoAbsencesFound searchText={searchString} />
      </div>
    );

  const getMinutesFromOffset = (offset: string): number => {
    const split = offset.split(':');
    return +split[0] * 60 + +split[1];
  };

  const buttonsDisabled = disabled || isUpdating || isLoading;

  return (
    <>
      <LateEarlyCreateOrUpdateDecisionDialog
        open={!!ambiguousAbsence}
        addClick={handleAddAbsenceToExisting}
        replaceClick={handleReplaceAbsence}
        cancelClick={handleCancelLateEarlyDecisionDialog}
        studentName={ambiguousAbsence?.affectedStudent.displayName}
        type={
          ambiguousAbsence?.absenceReason?.category as
            | AbsenceReasonCategory.late
            | AbsenceReasonCategory.leftEarly
        }
      />

      <StudentLeftEarlyDialog
        open={!!lateOrLeftEarlyAbsence}
        studentDisplayName={lateOrLeftEarlyAbsence?.affectedStudent.displayName ?? ''}
        onCloseClick={handleStudentLeftEarlyDialogCloseClick}
        onSaveClick={handleStudentLeftEarlyDialogSaveClick}
        minutes={
          lateOrLeftEarlyAbsence?.offset
            ? getMinutesFromOffset(lateOrLeftEarlyAbsence.offset)
            : undefined
        }
        lesson={currentLesson}
      />
      <div className="absenceEditDialogBackdrop">
        <div className="absenceEditDialog">
          <div className="flex">
            <Text size="xl" weight="bold">
              {t('classbook:absences:absence_edit_dialog_header', {
                defaultValue: 'Anwesenheit editieren',
              })}
            </Text>

            <div className="absenceListButtons">
              <Button
                className="absenceGridButton"
                aria-label={t('classbook:absences:absence_edit_dialog_view_grid', {
                  defaultValue: 'Rasteransicht',
                })}
                appearance={listpresentation === ListPresentation.Grid ? 'primary' : 'secondary'}
                onClick={() => onListPresentationChange(ListPresentation.Grid)}
                icon={<GridRegular />}
              />
              <Button
                aria-label={t('classbook:absences:absence_edit_dialog_view_list', {
                  defaultValue: 'Listenansicht',
                })}
                appearance={listpresentation === ListPresentation.List ? 'primary' : 'secondary'}
                onClick={() => onListPresentationChange(ListPresentation.List)}
                icon={<TextBulletListLtrRegular />}
              />
            </div>
          </div>

          <SearchInput
            appearance="filled-darker"
            className="studentSearchInput w-full"
            value={searchString}
            onChange={(_, data) => setSearchString(data?.value ?? '')}
            onClear={() => setSearchString('')}
            placeholder={t('search', { defaultValue: 'Suchen...' })}
          />
          {isLoading ? <Loader /> : content}

          <div className="flex">
            <Checkbox
              disabled={buttonsDisabled}
              label={t('classbook:absences:absence_edit_all_checkbox', {
                defaultValue: 'Alle nicht geprüften sind Anwesend',
              })}
              checked={allToPresent}
              onChange={(_, data) => setAllToPresent(data?.checked === true)}
            />
          </div>

          <div className="flex gap-2 justify-end items-center footer">
            <Button
              onClick={() => {
                onCloseClick();
                setVisibleAbsences(sortByPreference(visibleAbsences, preferenceSorting));
              }}
            >
              {t('cancel', { defaultValue: 'Abbrechen' })}
            </Button>
            <Button
              appearance="primary"
              onClick={handleConfirm}
              disabled={buttonsDisabled}
              loading={isUpdating}
            >
              {t('save', { defaultValue: 'Speichern' })}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
